import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, FileText, ListChecks, Plus, Wallet, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/badge.js';
import { Button } from '@/components/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card.js';
import { EmptyState } from '@/components/empty-state.js';
import { Page } from '@/components/page.js';
import { StatCard } from '@/components/stat-card.js';
import { api } from '@/lib/api.js';
import { formatCostUsd } from '@/lib/format-cost.js';

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const jobsQuery = useQuery({
    queryKey: ['jobs', 'list'],
    queryFn: () => api.jobs.list(),
  });
  const costQuery = useQuery({
    queryKey: ['stats', 'cost'],
    queryFn: () => api.stats.cost(),
  });
  const cost = costQuery.data;
  const jobs = jobsQuery.data ?? [];
  const recent = jobs.slice(0, 6);
  const readyCount = jobs.filter((j) => j.status === 'READY').length;

  return (
    <Page
      title={t('dashboard.title')}
      description={t('dashboard.description')}
      actions={
        <Button asChild>
          <Link to="/jobs/new">
            <Plus className="h-4 w-4" />
            {t('jobs.newJob')}
          </Link>
        </Button>
      }
    >
      {/* Stats row */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <StatCard
          icon={FileText}
          label={t('dashboard.totalJobs')}
          value={jobsQuery.isLoading ? '…' : jobs.length}
          tone="primary"
        />
        <StatCard
          icon={CheckCircle2}
          label={t('dashboard.readyJobs')}
          value={jobsQuery.isLoading ? '…' : readyCount}
          tone="emerald"
        />
        <StatCard
          icon={Zap}
          label={t('dashboard.apiStatus')}
          value={jobsQuery.isLoading ? '…' : jobsQuery.error ? t('dashboard.offline') : t('dashboard.online')}
          tone={jobsQuery.error ? 'red' : 'emerald'}
        />
      </div>

      {/* Spend row */}
      <div className="mb-8">
        <div className="mb-3 flex items-baseline gap-3">
          <h2 className="text-lg font-semibold">{t('dashboard.spend')}</h2>
          <span className="text-xs text-muted-foreground">
            {costQuery.isError ? t('dashboard.spendUnavailable') : t('dashboard.spendHint')}
          </span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            icon={Wallet}
            label={t('dashboard.spendToday')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : formatCostUsd(cost?.today)}
            tone="primary"
          />
          <StatCard
            icon={Wallet}
            label={t('dashboard.spendWeek')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : formatCostUsd(cost?.week)}
            tone="blue"
          />
          <StatCard
            icon={Wallet}
            label={t('dashboard.spendMonth')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : formatCostUsd(cost?.month)}
            tone="emerald"
          />
        </div>
      </div>

      {/* Tasks row — same ledger and windows as spend */}
      <div className="mb-8">
        <div className="mb-3 flex items-baseline gap-3">
          <h2 className="text-lg font-semibold">{t('dashboard.tasks')}</h2>
          <span className="text-xs text-muted-foreground">
            {costQuery.isError ? t('dashboard.spendUnavailable') : t('dashboard.tasksHint')}
          </span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            icon={ListChecks}
            label={t('dashboard.spendToday')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : (cost?.tasks.today ?? 0)}
            tone="primary"
          />
          <StatCard
            icon={ListChecks}
            label={t('dashboard.spendWeek')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : (cost?.tasks.week ?? 0)}
            tone="blue"
          />
          <StatCard
            icon={ListChecks}
            label={t('dashboard.spendMonth')}
            value={costQuery.isError ? '—' : costQuery.isLoading ? '…' : (cost?.tasks.month ?? 0)}
            tone="emerald"
          />
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('dashboard.recentJobs')}</h2>
        <Button asChild variant="ghost" size="sm">
          <Link to="/jobs">
            {t('dashboard.viewAll')} <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        </Button>
      </div>

      {jobsQuery.isLoading ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading')}</CardContent></Card>
      ) : recent.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileText}
            title={t('dashboard.noJobs')}
            desc={t('dashboard.noJobsDescription')}
            action={
              <Button asChild>
                <Link to="/jobs/new"><Plus className="h-4 w-4" />{t('jobs.newJob')}</Link>
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {recent.map((j) => (
            <Card key={j.id} className="hover:shadow-lift transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base truncate">{j.domain || j.name}</CardTitle>
                  <Badge variant={j.status === 'READY' ? 'success' : j.status === 'ARCHIVED' ? 'secondary' : 'outline'}>
                    {j.status}
                  </Badge>
                </div>
                <CardDescription>
                  {j.region} · {j.language} · {t('dashboard.articles', { count: j.articleCount })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" size="sm">
                  <Link to="/jobs/$jobId/edit" params={{ jobId: j.id }}>
                    {t('dashboard.open')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
